import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';

// Types
export type NotificationType =
  | 'LOW_STOCK'
  | 'OVERDUE_TASK'
  | 'EXPIRING_INVENTORY'
  | 'SYSTEM_ALERT'
  | 'WORK_ASSIGNED'
  | 'APPROVAL_REQUIRED'
  | 'VARIANCE_DETECTED'
  | 'SHIPMENT_READY'
  | 'RECEIPT_ARRIVED'
  | 'INFO'
  | 'ALLOCATION_FAILED'
  | 'BACKORDER_AGING'
  | 'CHAT_MESSAGE'
  | 'INVENTORY_RECEIVED'
  | 'QUEUE_POSITION_CHANGED'
  | 'RULE_CHANGED'
  | 'ORDER_CANCELLED'
  | 'PICK_WORK_CANCELLED'
  // Email-only opt-out key (nightly WMS updates email) — never an in-app notification.
  | 'WMS_UPDATES_DIGEST';

export type NotificationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface Notification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  warehouseId?: string;
  entityType?: string;
  entityId?: string;
  recipientId?: string;
  isRead: boolean;
  isDismissed: boolean;
  metadata?: Record<string, unknown>;
  expiresAt?: string;
  createdAt: string;
  readAt?: string;
  warehouse?: {
    id: string;
    name: string;
  };
}

export interface CreateNotificationInput {
  type: NotificationType;
  priority?: NotificationPriority;
  title: string;
  message: string;
  warehouseId?: string;
  entityType?: string;
  entityId?: string;
  recipientId?: string;
  metadata?: Record<string, unknown>;
  expiresAt?: string;
}

export interface NotificationFilters {
  warehouseId?: string;
  type?: NotificationType;
  priority?: NotificationPriority;
  isRead?: boolean;
  includeExpired?: boolean;
  skip?: number;
  take?: number;
}

export interface UnreadCount {
  total: number;
  byPriority: Record<NotificationPriority, number>;
  byType: Record<NotificationType, number>;
}

interface NotificationListResult {
  data: Notification[];
  total: number;
  unreadCount: number;
}

// Hooks
export function useNotifications(filters: NotificationFilters = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.warehouseId)
        params.append('warehouseId', filters.warehouseId);
      if (filters.type) params.append('type', filters.type);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.isRead !== undefined)
        params.append('isRead', String(filters.isRead));
      if (filters.includeExpired)
        params.append('includeExpired', String(filters.includeExpired));
      if (filters.skip) params.append('skip', String(filters.skip));
      if (filters.take) params.append('take', String(filters.take));

      const response = await apiClient.get<{
        notifications: Notification[];
        total: number;
        unreadCount: number;
      }>(`/api/notifications?${params.toString()}`);
      return {
        data: response.data.notifications,
        total: response.data.total,
        unreadCount: response.data.unreadCount,
      } as NotificationListResult;
    },
  });
}

export function useUnreadCount(warehouseId?: string) {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(warehouseId),
    queryFn: async () => {
      const params = warehouseId ? `?warehouseId=${warehouseId}` : '';
      const response = await apiClient.get<UnreadCount>(
        `/api/notifications/unread-count${params}`,
      );
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

interface CombinedUnreadCount {
  total: number;
  chat: number;
  system: number;
  systemByPriority: Record<string, number>;
  systemByType: Record<string, number>;
}

/**
 * Combined unread count — system + chat notifications.
 * Returns { total, chat } so callers can use combinedCount?.total / .chat.
 *
 * Uses a distinct cache key (`combinedUnreadCount`) so it does not collide
 * with `useUnreadCount`, which writes a different shape under `unreadCount`.
 */
export function useCombinedUnreadCount(warehouseId?: string) {
  return useQuery({
    queryKey: queryKeys.notifications.combinedUnreadCount(warehouseId),
    queryFn: async () => {
      const params = warehouseId ? `?warehouseId=${warehouseId}` : '';
      const response = await apiClient.get<CombinedUnreadCount>(
        `/api/notifications/combined-unread-count${params}`,
      );
      return response.data;
    },
    select: (data) => ({ total: data.total, chat: data.chat }),
    refetchInterval: 30000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const response = await apiClient.post<{ markedCount: number }>(
        '/api/notifications/mark-read',
        {
          notificationIds,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the whole `notifications` key space (lists + unreadCount +
      // combinedUnreadCount) so the bell badge — which reads combinedUnreadCount
      // — refreshes immediately, not on the 30s refetch.
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (warehouseId?: string) => {
      const params = warehouseId ? `?warehouseId=${warehouseId}` : '';
      const response = await apiClient.post<{ markedCount: number }>(
        `/api/notifications/mark-all-read${params}`,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the whole `notifications` key space (lists + unreadCount +
      // combinedUnreadCount) so the bell badge — which reads combinedUnreadCount
      // — refreshes immediately, not on the 30s refetch.
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      });
    },
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.put<Notification>(
        `/api/notifications/${id}/dismiss`,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the whole `notifications` key space (lists + unreadCount +
      // combinedUnreadCount) so the bell badge — which reads combinedUnreadCount
      // — refreshes immediately, not on the 30s refetch.
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      });
    },
  });
}
